import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const stappen = [
  {
    number: '01',
    icon: 'ri-send-plane-line',
    title: 'Stuur je product of idee door',
    subtitle: 'Vandaag nog starten',
    description: 'Heb je een product in gedachten? Stuur ons een foto, schets, link of beschrijving. Geen ervaring nodig — wij begrijpen ook vage ideeën.',
    details: [
      'Stuur via WhatsApp, e-mail of het contactformulier',
      'Foto, schets, referentielink of beschrijving volstaat',
      'Geen technische kennis vereist',
      'Wij stellen de juiste vragen om alles te verduidelijken',
    ],
    duur: 'Vandaag nog',
    color: 'bg-[#3B9EE8]/10',
  },
  {
    number: '02',
    icon: 'ri-search-eye-line',
    title: 'Wij doen een prijscheck in China',
    subtitle: '1–2 werkdagen',
    description: 'Ons netwerk van fabrikanten wordt direct ingezet. We vergelijken meerdere leveranciers op prijs, kwaliteit en levertijd.',
    details: [
      'Vergelijking van meerdere fabrieken',
      'Beoordeling op prijs-kwaliteitsverhouding',
      'Controle op minimale bestelhoeveelheid (MOQ)',
      'Beoordeling van productiecapaciteit',
    ],
    duur: '1–2 werkdagen',
    color: 'bg-[#3B9EE8]/10',
  },
  {
    number: '03',
    icon: 'ri-file-list-3-line',
    title: 'Je ontvangt een helder voorstel',
    subtitle: 'Binnen 24 uur',
    description: 'Geen ingewikkeld jargon. Je krijgt een overzichtelijk voorstel met alle kosten, levertijd en voorwaarden — transparant en begrijpelijk.',
    details: [
      'All-in prijs inclusief transport en invoerrechten (DDP)',
      'Verwachte levertijd en productieperiode',
      'Minimum bestelhoeveelheid en opties',
      'Optie voor sample aanvraag',
    ],
    duur: 'Binnen 24 uur',
    color: 'bg-[#3B9EE8]/10',
  },
  {
    number: '04',
    icon: 'ri-tools-line',
    title: 'Sample & goedkeuring',
    subtitle: 'Aanbevolen stap',
    description: 'Voordat je een grote bestelling plaatst, raden wij altijd een sample aan. Zo zie je precies wat je krijgt, en kun je aanpassingen doorgeven.',
    details: [
      'Samplebeoordeling op kwaliteit en afwerking',
      'Feedbackverwerking bij afwijkingen',
      'Goedkeuring voor productiestart',
      'Verplichte stap bij maatwerk of private label',
    ],
    duur: '1–2 weken',
    color: 'bg-[#3B9EE8]/10',
  },
  {
    number: '05',
    icon: 'ri-settings-3-line',
    title: 'Productie & kwaliteitscontrole',
    subtitle: 'Wij houden alles in de gaten',
    description: 'Na goedkeuring start de productie. Wij begeleiden het proces, voeren steekproefsgewijze kwaliteitscontroles uit en houden jou op de hoogte.',
    details: [
      'Productiecoördinatie namens jou',
      'Steekproefsgewijze kwaliteitscontroles',
      'Regelmatige statusupdates',
      'Documentatie bij afwijkingen',
    ],
    duur: 'Afhankelijk van product',
    color: 'bg-[#3B9EE8]/10',
  },
  {
    number: '06',
    icon: 'ri-truck-line',
    title: 'Levering aan je deur',
    subtitle: 'All-in & zonder gedoe',
    description: 'Jouw bestelling wordt DDP geleverd aan jouw adres in Nederland. Invoerrechten, BTW en transport zijn al inbegrepen. Jij hoeft niks te regelen.',
    details: [
      'DDP levering — alles inbegrepen',
      'Track & trace informatie per zending',
      'Bezorging aan jouw deur in Nederland',
      'Nazorg bij eventuele vragen of problemen',
    ],
    duur: 'All-in prijs',
    color: 'bg-[#3B9EE8]/10',
  },
];

const faqs = [
  { q: 'Hoe lang duurt het gemiddeld?', a: 'Dat verschilt per product. Na goedkeuring is de productietijd gemiddeld 2–6 weken, plus 2–4 weken transport. Wij geven altijd een indicatieve levertijd in het voorstel.' },
  { q: 'Kan ik ook starten als particulier?', a: 'Absoluut. Je hoeft geen bedrijf te hebben om via DUC in te kopen. Iedereen is welkom.' },
  { q: 'Wat als het sample niet goed is?', a: 'Dan bespreken wij de aanpassingen met de fabrikant en vragen een nieuw sample aan. Productie start pas na jouw goedkeuring.' },
  { q: 'Zijn de levertijden gegarandeerd?', a: 'Nee, opgegeven levertijden zijn indicatief. Externe factoren zoals productie, transport en douane kunnen invloed hebben. Zie ook onze Algemene Voorwaarden.' },
];

function StapCard({ stap, index }: { stap: typeof stappen[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-2xl border border-[#0A1628]/6 hover:border-[#3B9EE8]/30 transition-all duration-300 overflow-hidden"
    >
      <div className="flex flex-col md:flex-row">
        <div className="flex-shrink-0 bg-[#0A1628] p-8 md:w-48 flex flex-col items-center justify-center gap-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#3B9EE8]/20 border-2 border-[#3B9EE8]/50">
            <span className="font-heading font-800 text-[#3B9EE8] text-lg">{stap.number}</span>
          </div>
          <div className="w-10 h-10 flex items-center justify-center">
            <i className={`${stap.icon} text-[#3B9EE8] text-2xl`} />
          </div>
          <div className="bg-[#3B9EE8]/15 rounded-full px-3 py-1 text-center">
            <span className="text-[#3B9EE8] text-xs font-600">{stap.duur}</span>
          </div>
        </div>
        <div className="flex-1 p-8">
          <p className="text-[#3B9EE8] text-xs font-700 uppercase tracking-widest mb-2">{stap.subtitle}</p>
          <h3 className="font-heading font-800 text-[#0A1628] text-xl md:text-2xl mb-3">{stap.title}</h3>
          <p className="text-[#0A1628]/60 text-sm leading-relaxed mb-5">{stap.description}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {stap.details.map((detail) => (
              <li key={detail} className="flex items-start gap-2">
                <i className="ri-check-line text-[#3B9EE8] text-sm mt-0.5 flex-shrink-0" />
                <span className="text-[#0A1628]/65 text-xs font-500">{detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

export default function HoeHetWerktPage() {
  useSEO({
    title: 'Hoe het werkt | Inkopen uit China in 6 Stappen via DUC',
    description: 'Hoe werkt inkopen uit China via DUC? In 6 stappen van idee naar levering aan uw deur. Product sourcing, sampling, productie, kwaliteitscontrole en DDP levering. Transparant en eenvoudig.',
    keywords: 'hoe werkt inkopen China, stappenplan import China, product sourcing werkwijze, DUC werkwijze, inkopen procedure China, van idee naar levering China',
    canonical: `${SITE_URL}/hoe-het-werkt`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Hoe het werkt', item: `${SITE_URL}/hoe-het-werkt` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'Hoe inkopen uit China via DUC in 6 stappen',
        description: 'Van eerste contact tot levering aan uw deur — DUC regelt het complete traject zonder gedoe.',
        url: `${SITE_URL}/hoe-het-werkt`,
        step: [
          { '@type': 'HowToStep', name: 'Stuur je product of idee door', text: 'Stuur ons een foto, schets, link of beschrijving van uw product.' },
          { '@type': 'HowToStep', name: 'Wij doen een prijscheck in China', text: 'Ons netwerk van fabrikanten wordt direct ingezet. We vergelijken meerdere leveranciers op prijs, kwaliteit en levertijd.' },
          { '@type': 'HowToStep', name: 'U ontvangt een helder voorstel', text: 'Binnen 24 uur een overzichtelijk voorstel met alle kosten, levertijd en voorwaarden — transparant en begrijpelijk.' },
          { '@type': 'HowToStep', name: 'Sample & goedkeuring', text: 'Beoordeel het product voordat u een grote bestelling plaatst. Wij raden altijd een sample aan.' },
          { '@type': 'HowToStep', name: 'Productie & kwaliteitscontrole', text: 'Na goedkeuring start de productie. Wij begeleiden het proces en voeren steekproefsgewijze kwaliteitscontroles uit.' },
          { '@type': 'HowToStep', name: 'Levering aan uw deur', text: 'DDP levering aan uw adres in Nederland. Invoerrechten, BTW en transport zijn al inbegrepen.' },
        ],
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-14 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/976d808d29316a5426f6d8133ed35415.jpg"
            alt="Hoe het werkt"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line" />
              Terug naar home
            </Link>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-route-line" />
            Hoe het werkt
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
            Van idee naar levering<br />
            <span className="text-[#3B9EE8]">in 6 eenvoudige stappen</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/60 text-lg max-w-2xl leading-relaxed">
            Geen verrassingen, geen gedoe. DUC regelt het complete traject — van eerste contact tot levering aan jouw deur.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-[#F7F8FA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col gap-6">
            {stappen.map((stap, i) => (
              <StapCard key={stap.number} stap={stap} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-4">
              <i className="ri-question-line" />
              Veelgestelde vragen
            </span>
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl mb-4">Vragen over het proces?</h2>
          </div>
          <div className="flex flex-col gap-4">
            {faqs.map((faq) => (
              <div key={faq.q} className="bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-6">
                <h4 className="font-700 text-[#0A1628] text-base mb-2 flex items-start gap-2">
                  <i className="ri-question-line text-[#3B9EE8] text-base mt-0.5 flex-shrink-0" />
                  {faq.q}
                </h4>
                <p className="text-[#0A1628]/60 text-sm leading-relaxed pl-6">{faq.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/faq" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm hover:underline cursor-pointer">
              Bekijk alle vragen
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Klaar om te beginnen?
          </h2>
          <p className="text-white/55 text-lg mb-8">
            Stuur ons je product en ontvang binnen 24 uur een gratis prijsindicatie.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="inline-flex items-center justify-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-mail-send-line" />
              Stuur je product door
            </a>
            <a href="https://wa.me/31624359162" target="_blank" rel="nofollow noreferrer" className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-[#3B9EE8]/60 hover:text-[#3B9EE8] px-8 py-4 rounded-lg font-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-xl" />
              WhatsApp ons
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
